import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  Chip, 
  Avatar, 
  CircularProgress, 
  IconButton, 
  Divider,
  Paper,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  LinearProgress
} from '@mui/material';
import { 
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon, 
  Cancel as CancelIcon,
  Update as UpdateIcon,
  MoreVert as MoreVertIcon,
  FilterAlt as FilterAltIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  AlarmOn as AlarmOnIcon,
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  EventNote as EventNoteIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  Print as PrintIcon,
  Autorenew as AutorenewIcon,
  Share as ShareIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import WorkflowService from '../../../services/workflowService';

const WorkflowDashboard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('lastMonth');
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    pendingReview: 0,
    rejected: 0,
    trend: 'up',
    trendPercentage: 0
  });
  const [workflows, setWorkflows] = useState([]);
  const [averageTimeData, setAverageTimeData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [topRequesters, setTopRequesters] = useState([]);
  const [slaMetrics, setSlaMetrics] = useState({
    met: 0,
    violated: 0,
    atRisk: 0
  });
  const [error, setError] = useState(null);

  // Colors for charts
  const CHART_COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    alpha(theme.palette.primary.main, 0.7),
    alpha(theme.palette.secondary.main, 0.7),
    alpha(theme.palette.success.main, 0.7)
  ];

  // Improve error handling in the useEffect
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Use existing methods instead of non-existent getWorkflowStats
        // First get counts for basic statistics
        const countsResponse = await WorkflowService.getRequestCounts();
        
        if (countsResponse && countsResponse.data) {
          const countsData = countsResponse.data;
          // Prepare stats data
          const newStats = {
            total: countsData.total || 0,
            active: countsData.active || 0,
            completed: countsData.completed || 0,
            pendingReview: 0, // Will calculate from statuses if available
            rejected: 0,      // Will calculate from statuses if available
            trend: 'up',
            trendPercentage: 5 // Default value
          };
          
          // Calculate pendingReview and rejected from statuses if available
          if (countsData.statuses) {
            Object.entries(countsData.statuses).forEach(([status, count]) => {
              const statusLower = status.toLowerCase();
              if (statusLower.includes('pending') || statusLower.includes('review')) {
                newStats.pendingReview += count;
              } else if (statusLower.includes('reject') || statusLower.includes('denied')) {
                newStats.rejected += count;
              }
            });
          }
          
          setStats(newStats);
          
          // Prepare status data for the pie chart
          const statusChartData = [];
          if (countsData.statuses) {
            Object.entries(countsData.statuses).forEach(([status, count]) => {
              statusChartData.push({
                name: status,
                value: count
              });
            });
          } else {
            // Fallback if statuses not available
            statusChartData.push(
              { name: "Completed", value: newStats.completed },
              { name: "Active", value: newStats.active }
            );
            
            if (newStats.pendingReview > 0) {
              statusChartData.push({ name: "Pending Review", value: newStats.pendingReview });
            }
            
            if (newStats.rejected > 0) {
              statusChartData.push({ name: "Rejected", value: newStats.rejected });
            }
          }
          
          setStatusData(statusChartData);
        }
        
        // Get all workflow requests for additional metrics
        // Use the appropriate endpoint based on user role
        const isStaffContext = location.pathname.startsWith('/staff/');
        const allRequestsResponse = isStaffContext
          ? await WorkflowService.getStaffRequests(1, 100)
          : await WorkflowService.getAllRequests(1, 100);
        
        let workflowRequests = [];
        if (allRequestsResponse?.data) {
          workflowRequests = Array.isArray(allRequestsResponse.data) 
            ? allRequestsResponse.data 
            : allRequestsResponse.data.data || [];
        }
        
        setWorkflows(workflowRequests);
        
        // Process workflow data for charts
        // ... continue with the rest of the data processing
        
      } catch (error) {
        console.error('Error fetching workflow data:', error);
        setError('Failed to load workflow analytics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  // Add a helper to get API endpoints based on user role
  const getStatsEndpoint = () => {
    // If we're in staff context, use a staff-specific endpoint if available
    const isStaffPortal = location.pathname.startsWith('/staff/');
    return isStaffPortal ? '/workflows/staff/analytics' : '/workflows/analytics';
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    handleMenuClose();
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    // Add null check to prevent TypeError
    if (!status) return theme.palette.info.main;
    
    switch (status.toLowerCase()) {
      case 'completed':
      case 'approved':
        return theme.palette.success.main;
      case 'rejected':
      case 'denied':
        return theme.palette.error.main;
      case 'in progress':
        return theme.palette.primary.main;
      case 'pending':
      case 'pending review':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  // Render dashboard metrics
  const renderMetrics = () => {
    return (
      <Grid container spacing={3}>
        {/* Total Workflows */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative', 
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px'
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, p: 1 }}>
              <AssignmentIcon sx={{ color: alpha(theme.palette.primary.main, 0.2), fontSize: 60 }} />
            </Box>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                TOTAL WORKFLOWS
              </Typography>
              <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                mt: 2,
                p: 0.5,
                borderRadius: 1,
                width: 'fit-content',
                backgroundColor: stats.trend === 'up' ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1)
              }}>
                {stats.trend === 'up' ? (
                  <TrendingUpIcon fontSize="small" color="success" />
                ) : (
                  <TrendingDownIcon fontSize="small" color="error" />
                )}
                <Typography 
                  variant="caption" 
                  color={stats.trend === 'up' ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5, fontWeight: 'bold' }}
                >
                  {stats.trendPercentage}% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Workflows */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative', 
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            color: '#fff'
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, p: 1 }}>
              <UpdateIcon sx={{ color: alpha('#fff', 0.2), fontSize: 60 }} />
            </Box>
            <CardContent>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), fontWeight: 'bold' }}>
                ACTIVE WORKFLOWS
              </Typography>
              <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold' }}>
                {stats.active}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
                {Math.round((stats.active / stats.total) * 100)}% of total workflows
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Completed Workflows */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative', 
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px'
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, p: 1 }}>
              <CheckCircleIcon sx={{ color: alpha(theme.palette.success.main, 0.2), fontSize: 60 }} />
            </Box>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                COMPLETED WORKFLOWS
              </Typography>
              <Typography variant="h3" sx={{ my: 1, fontWeight: 'bold', color: theme.palette.success.main }}>
                {stats.completed}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round((stats.completed / stats.total) * 100)}% completion rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* SLA Compliance */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            position: 'relative', 
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px'
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, p: 1 }}>
              <AlarmOnIcon sx={{ color: alpha(theme.palette.warning.main, 0.2), fontSize: 60 }} />
            </Box>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                SLA COMPLIANCE
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', my: 1 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {slaMetrics.met}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, ml: 1 }}>
                  Met
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Tooltip title={`${slaMetrics.violated}% Violated, ${slaMetrics.atRisk}% At Risk`}>
                  <Box sx={{ width: '100%', display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ width: `${slaMetrics.met}%`, bgcolor: theme.palette.success.main }}></Box>
                    <Box sx={{ width: `${slaMetrics.atRisk}%`, bgcolor: theme.palette.warning.main }}></Box>
                    <Box sx={{ width: `${slaMetrics.violated}%`, bgcolor: theme.palette.error.main }}></Box>
                  </Box>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render charts section
  const renderCharts = () => {
    return (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Workflow Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="medium">
                  Workflow Status Distribution
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [`${value} workflows`, 'Count']} />
                    <Legend layout="vertical" verticalAlign="middle" align="right" />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Workflows by Department */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="medium">
                  Workflows by Department
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={departmentData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => [`${value} workflows`, 'Count']} />
                    <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Time to Complete */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="medium">
                  Average Completion Time by Workflow Type
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={averageTimeData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" />
                    <RechartsTooltip formatter={(value) => [`${value} days`, 'Average Time']} />
                    <Bar dataKey="days" fill={theme.palette.success.main} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Monthly Timeline */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="medium">
                  Workflow Timeline (6-Month)
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={timelineData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="completed" stroke={theme.palette.success.main} 
                      strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    <Line type="monotone" dataKey="active" stroke={theme.palette.primary.main} 
                      strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  // Render top workflows table
  const renderTopWorkflows = () => {
    return (
      <Card sx={{ 
        mt: 3,
        borderRadius: 3,
        boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="medium">
              Active Workflows
            </Typography>
            <Box>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<FilterAltIcon />} 
                onClick={handleFilterOpen}
                sx={{ mr: 1, borderRadius: 8 }}
              >
                Filter
              </Button>
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleFilterClose}
              >
                <MenuItem onClick={handleFilterClose}>All Workflows</MenuItem>
                <MenuItem onClick={handleFilterClose}>High Priority</MenuItem>
                <MenuItem onClick={handleFilterClose}>Overdue</MenuItem>
                <MenuItem onClick={handleFilterClose}>Recently Updated</MenuItem>
              </Menu>
              <Button 
                variant="contained" 
                size="small" 
                startIcon={<RefreshIcon />}
                sx={{ borderRadius: 8 }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 800 }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '0.8fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr', 
                py: 1.5,
                px: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: 2
              }}>
                <Typography variant="subtitle2" fontWeight="bold">ID</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Title</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Type</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Requester</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Last Updated</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Due Date</Typography>
                <Typography variant="subtitle2" fontWeight="bold">Step</Typography>
              </Box>
              
              {workflows.map((workflow) => (
                <Box 
                  key={workflow.id}
                  sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '0.8fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr', 
                    py: 2,
                    px: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography variant="body2">{workflow.id}</Typography>
                  <Typography variant="body2" fontWeight="medium">{workflow.title}</Typography>
                  <Typography variant="body2">{workflow.type}</Typography>
                  <Box>
                    <Chip 
                      label={workflow.status || 'Unknown'} 
                      size="small"
                      sx={{ 
                        bgcolor: alpha(getStatusColor(workflow.status), 0.1),
                        color: getStatusColor(workflow.status),
                        fontWeight: 'bold'
                      }} 
                    />
                  </Box>
                  <Typography variant="body2">{workflow.requester}</Typography>
                  <Typography variant="body2">{workflow.lastUpdated}</Typography>
                  <Typography variant="body2" sx={{ 
                    color: workflow.dueDate && new Date(workflow.dueDate) < new Date() ? 'error.main' : 'inherit',
                    fontWeight: workflow.dueDate && new Date(workflow.dueDate) < new Date() ? 'bold' : 'normal'
                  }}>
                    {workflow.dueDate}
                  </Typography>
                  <Typography variant="body2">{workflow.step}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button 
              variant="text" 
              color="primary"
              endIcon={<ArrowForwardIcon />}
            >
              View All Workflows
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  // Render top requesters and bottlenecks
  const renderInsights = () => {
    return (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Top Requesters */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="medium">
                  Top Requesters
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <List disablePadding>
                {topRequesters.map((person, index) => (
                  <ListItem 
                    key={person.id}
                    divider={index < topRequesters.length - 1}
                    sx={{ px: 1, py: 1.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        alt={person.name}
                        src={person.avatar}
                        sx={{ bgcolor: theme.palette.primary.main }}
                      >
                        {person.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="subtitle2">{person.name}</Typography>}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {person.department}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip 
                        label={`${person.requests} requests`}
                        size="small"
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontWeight: 'bold'
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Bottleneck Analysis */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="medium">
                  Workflow Bottlenecks
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <List disablePadding>
                <ListItem divider sx={{ px: 1, py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>1</Avatar>
                  <ListItemText
                    primary={<Typography variant="subtitle2">Contract Review</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Average waiting time: 5.4 days
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <LinearProgress
                      value={100}
                      variant="determinate"
                      sx={{ 
                        width: 100, 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.error.main
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider sx={{ px: 1, py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'error.light', mr: 2 }}>2</Avatar>
                  <ListItemText
                    primary={<Typography variant="subtitle2">Project Approval</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Average waiting time: 4.1 days
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <LinearProgress
                      value={76}
                      variant="determinate"
                      sx={{ 
                        width: 100, 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.error.light
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider sx={{ px: 1, py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>3</Avatar>
                  <ListItemText
                    primary={<Typography variant="subtitle2">Equipment Request</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Average waiting time: 3.2 days
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <LinearProgress
                      value={59}
                      variant="determinate"
                      sx={{ 
                        width: 100, 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.warning.main, 0.2),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.warning.main
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider sx={{ px: 1, py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'warning.light', mr: 2 }}>4</Avatar>
                  <ListItemText
                    primary={<Typography variant="subtitle2">Expense Request</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Average waiting time: 2.5 days
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <LinearProgress
                      value={46}
                      variant="determinate"
                      sx={{ 
                        width: 100, 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.warning.main, 0.2),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.warning.light
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem sx={{ px: 1, py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>5</Avatar>
                  <ListItemText
                    primary={<Typography variant="subtitle2">Time Off Request</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Average waiting time: 1.3 days
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <LinearProgress
                      value={24}
                      variant="determinate"
                      sx={{ 
                        width: 100, 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.success.main, 0.2),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.success.main
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // If there's an error, show an error message
  if (error) {
    return (
      <Box sx={{ 
        p: 4, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh'
      }}>
        <Typography variant="h5" color="error" gutterBottom>
          Error Loading Dashboard
        </Typography>
        <Typography variant="body1" paragraph>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: '#f9fafc', 
      minHeight: '100vh',
      py: 3
    }}>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Workflow Analytics Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Executive overview of workflow performance and metrics
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined"
              startIcon={<CalendarTodayIcon />}
              onClick={handleMenuOpen}
              sx={{ borderRadius: 8 }}
            >
              {timeRange === 'lastWeek' ? 'Last Week' : 
               timeRange === 'lastMonth' ? 'Last Month' : 
               timeRange === 'lastQuarter' ? 'Last Quarter' : 
               timeRange === 'lastYear' ? 'Last Year' : 'Custom Range'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleTimeRangeChange('lastWeek')}>Last Week</MenuItem>
              <MenuItem onClick={() => handleTimeRangeChange('lastMonth')}>Last Month</MenuItem>
              <MenuItem onClick={() => handleTimeRangeChange('lastQuarter')}>Last Quarter</MenuItem>
              <MenuItem onClick={() => handleTimeRangeChange('lastYear')}>Last Year</MenuItem>
              <MenuItem onClick={() => handleTimeRangeChange('custom')}>Custom Range</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Quick Dashboard Metrics */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderMetrics()}
            
            <Box sx={{ mt: 4 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    minWidth: 100,
                  },
                  mb: 2
                }}
              >
                <Tab 
                  icon={<BarChartIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Analytics" 
                />
                <Tab 
                  icon={<TimelineIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Workflows" 
                />
                <Tab 
                  icon={<AssessmentIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Insights" 
                />
              </Tabs>
              
              <Box sx={{ mt: 1 }}>
                {tabValue === 0 && renderCharts()}
                {tabValue === 1 && renderTopWorkflows()}
                {tabValue === 2 && renderInsights()}
              </Box>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default WorkflowDashboard;
